import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { type DrawerProps } from '@mui/material';
import { SButton } from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { CloseButton } from '@bbkAdminComponents/buttons';
import {
  ConfirmationDialog,
  DialogActions,
  DialogContent,
  DialogError,
  DialogTitle,
} from '@bbkAdminComponents/editor-page-overlay/dialog';
import { Label } from '@bbkAdminComponents/inputs/label/label';
import TimePicker from '@bbkAdminComponents/popovers/time-picker';
import { GenericSelect } from '@bbkAdminComponents/select/select';
import {
  selectAppId,
  selectCompanyInternalName,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import { useSelectDatePicker } from '@bbkAdminRedux/rtkq/datepicker-persist.slice';
import { useExportCustomersData } from '@bbkAdminRedux/rtkq/reports.slice';
import { segmentCustomEvent } from '@bbkAdminUtils/analytics';
import { type CustomerReportOpts } from '@bbkAdminUtils/api-client/reports-api';
import { Icons } from '@bbkAdminUtils/icons';

type Props = Required<Pick<DrawerProps, 'open'>> & {
  onClose: () => void;
  reportOpts?: Partial<CustomerReportOpts>;
};

type formatItem = {
  label: 'CSV' | 'JSON';
  value: 'CSV' | 'JSON';
};
const formatOptions: formatItem[] = [
  {
    label: 'CSV',
    value: 'CSV',
  },
  {
    label: 'JSON',
    value: 'JSON',
  },
];

export const ExportCustomerDataDialog: React.FC<Props> = (props) => {
  const { open, onClose, reportOpts = {} } = props;
  const timePicker = useSelectDatePicker();
  const appId = useSelector(selectAppId);
  const companyId = useSelector(selectCompanyInternalName);
  const user = useSelector(selectUser);
  const [error, setError] = useState();
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportFormat, setExportFormat] = useState<formatItem>(
    formatOptions[0] as formatItem
  );

  const userEmail = user?.email;
  const exportCustomersData = useExportCustomersData();

  function exportData() {
    setExportInProgress(true);
    setError(undefined);

    const pageCustomerReportOpts: CustomerReportOpts = {
      start: timePicker.startDate,
      end: timePicker.endDate,
      format: exportFormat.value,
      ...reportOpts,
    };

    segmentCustomEvent('Customers Page Enhanced Export', {
      ...pageCustomerReportOpts,
      appId,
      companyId,
    });

    BlackToastTrigger({
      content: `Export generation in progress. This can take several minutes. Leave the export dialog open to save directly. An additional link will be emailed to ${userEmail} as well. Closing the export dialog will not stop the email.`,
      options: { autoClose: 8000 },
    });

    return exportCustomersData(pageCustomerReportOpts, {
      appId,
      companyId,
      returnDownloadUrl: true,
    })
      .then((downloadUrl) => {
        if (open) {
          window.location.href = downloadUrl as string;
          onClose();
        }
      })
      .catch((e: Error) => {
        console.error(e.message);
      })
      .finally(() => {
        setExportInProgress(false);
      });
  }

  const copy = {
    title: 'Generate customer export',
    content: `Exports may take a few minutes to generate depending on date range in question. Leave this dialog open to save a copy directly. Please check your email (${userEmail}) for a download link as well.`,
    button: 'Generate export',
    buttonActive: 'Generating export...',
  };

  return (
    <ConfirmationDialog open={open}>
      <DialogTitle>
        <div className="bbk-brand-management-conf-dialog-title-text">
          {copy.title}
        </div>
        <CloseButton autoFocus onClick={onClose} />
      </DialogTitle>
      <DialogContent className="[&_.timeAgoButton]:tw-w-auto [&_.timeAgoButton]:tw-border-black/10">
        <Label id="timeRange" label="Time range" />
        <TimePicker
          hidePicker
          readOnly
          className="tw-mb-1 !tw-inline-block tw-rounded tw-border tw-border-cbrneutral-300"
        />
        <Label id="format" label="Export format" />
        <div className="tw-max-w-1/2">
          <GenericSelect
            isSearchable={false}
            options={formatOptions}
            value={exportFormat}
            onChange={(value) => setExportFormat(value as formatItem)}
          />
        </div>
        <div className="tw-mt-4 tw-text-xs">{copy.content}</div>
      </DialogContent>
      {error && <DialogError>{error}</DialogError>}
      <DialogActions>
        <SButton
          variant="primary"
          onClick={exportData}
          disabled={exportInProgress}
        >
          {exportInProgress ? (
            <>
              {Icons.loaderSW6} {copy.buttonActive}
            </>
          ) : (
            copy.button
          )}
        </SButton>
      </DialogActions>
    </ConfirmationDialog>
  );
};
